/**
 * Native gallery bridge — Capacitor plugin facade.
 *
 * On the Android wrapper this resolves to the AgentChatGallery native
 * plugin (see native-android/plugin/AgentChatGalleryPlugin.java) which
 * queries `MediaStore.Images.Media` for the
 * device's recent photos. On a plain PWA this resolves to the stub
 * web fallback below — `getRecentImages()` returns `{ images: [] }`,
 * so AttachMenu cleanly falls back to the IDB-cached strip.
 *
 * Why a fallback at all: this same code runs in dev / Vercel previews /
 * any context where Capacitor's runtime isn't present. `registerPlugin`
 * with a `web` proxy keeps the surface identical so the caller never
 * has to feature-detect.
 */

import { Capacitor, registerPlugin } from '@capacitor/core'

export type NativeGalleryItem = {
  /** MediaStore id — opaque handle for `loadFullImage()`. */
  id: string
  /** Original mime type from the device. */
  mimeType: string
  /** When the photo was taken (or, for screenshots, captured). */
  dateAddedMs: number
  /** Original filename if available — for nicer chip labels. */
  filename: string | null
  /** Base64 JPEG of a small thumbnail (max 256 px on the long edge). */
  thumbBase64: string
  /** Original-pixel width/height — used by AttachMenu only for layout hints. */
  width: number
  height: number
}

export interface AgentChatGalleryPlugin {
  /** Recent images from MediaStore, newest first. Honors the user's
   *  Photos permission grant; throws on denied. */
  getRecentImages(options: { limit?: number }): Promise<{
    images: NativeGalleryItem[]
    permissionGranted: boolean
    rawCount?: number
    firstError?: string
  }>

  /** Pull the full-size bytes for the picked id and hand them back as a
   *  base64 string. The frontend wraps that into a real File and runs
   *  it through the existing _processFiles → editor → upload pipeline. */
  loadFullImage(options: { id: string }): Promise<{
    base64: string
    mimeType: string
    filename: string
    sizeBytes: number
  }>

  /** Trigger the native runtime permission prompt. Resolves with the
   *  current state ("granted" | "denied" | "prompt"). */
  requestPermission(): Promise<{ state: 'granted' | 'denied' | 'prompt' }>
}

const AgentChatGallery = registerPlugin<AgentChatGalleryPlugin>('AgentChatGallery', {
  web: () => Promise.resolve({
    getRecentImages: async () => ({ images: [], permissionGranted: false }),
    loadFullImage: async () => {
      throw new Error('loadFullImage is unavailable in the web build')
    },
    requestPermission: async () => ({ state: 'denied' as const }),
  }),
})

/**
 * True only inside the Capacitor-wrapped Android app. AttachMenu uses
 * this to decide whether to render the native gallery row at all —
 * leaving the web build untouched.
 */
export function isNativeGalleryAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AgentChatGallery')
}

/** Decode a NativeGalleryItem's payload into a real File for the
 *  existing image-pick pipeline. Reads via the plugin instead of fetch
 *  because content:// URIs aren't directly usable in <img> + fetch on
 *  every Android version. */
export async function nativeImageToFile(item: NativeGalleryItem): Promise<File> {
  const result = await AgentChatGallery.loadFullImage({ id: item.id })
  const buffer = _base64ToArrayBuffer(result.base64)
  return new File([buffer], result.filename || `${item.id}.jpg`, {
    type: result.mimeType || item.mimeType || 'image/jpeg',
    lastModified: item.dateAddedMs || Date.now(),
  })
}

function _base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64)
  const buffer = new ArrayBuffer(bin.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return buffer
}

export { AgentChatGallery }
